import { useMemo, useState } from 'react';
import { OFF } from '../../lib/off';
import { Link, useNavigate, useSearchParams } from 'react-router';
import {
  AGENT_FEATURE_SET,
  ALL_FEATURE_SETS,
  FEATURE_SETS,
  MAILBOX_FEATURE_SET,
  SHARED_PHONE_KINDS,
  displayOf,
  formatPhoneNumber,
  isInternalRoute,
  sharedPhoneFeatureSet,
  sharedPhoneKindLabel,
  type SharedPhoneKind
} from '@stitch/pbx-adapter/catalog';
import { suggestNumber } from '@stitch/pbx-adapter/describe';
import { companyLengthFrom, extensionHint, extensionProblem } from '@stitch/pbx-adapter/extensions';
import { PHONE_MODELS, normalizeMac, phoneModel } from '@stitch/pbx-adapter/phones';
import { ApiError, api, placesApi, type Account, type ChangeSetView, type Intent, type InviteResult } from '../../api/client';
import { DestinationPicker } from '../../components/pickers';
import { ReviewDrawer, type ReviewItem } from '../../components/ReviewDrawer';
import { useTenant } from '../../components/Shell';
import { Button, Card, Field, LoadError, Loading, Notice, PageHeader } from '../../components/ui';
import { Icon } from '../../lib/icons';
import { useData } from '../../lib/useData';
import { pbxZoneNames } from './People';
import { ShareLink } from './Team';

const STEPS = [
  { id: 'who', title: 'Who they are' },
  { id: 'number', title: 'Phone number' },
  { id: 'calls', title: 'How calls reach them' },
  { id: 'phone', title: 'Desk phone' },
  { id: 'access', title: 'Sign-in and welcome' },
  { id: 'review', title: 'Review' }
] as const;
type StepId = (typeof STEPS)[number]['id'];

interface Draft {
  firstName: string;
  lastName: string;
  email: string;
  mobile: string;
  extension: string;
  featureSet: string;
  timeZone: string;
  /** A number of the company's that will ring them; '' for none. */
  number: string;
  /** Show their own number when they call out ('own'), or the company's usual (''). */
  callerId: '' | 'own';
  /** Their phone's emergency (911) address: one of the company's saved ones ('' for not now). */
  location: string;
  groups: string[];
  queues: string[];
  noAnswerSeconds: string;
  forwardNoAnswer: string;
  phone: { mac: string; model: string } | null;
  welcome: boolean;
  portal: boolean;
  /** A shared phone (1.13.0, 3067): a place, named in firstName; no person, no sign-in. */
  place: boolean;
  /** Its kind; its extension type (featureSet) follows it. */
  placeKind: SharedPhoneKind;
}

const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const words = (v: string | undefined) => (v ?? '').split(/\s+/).filter(Boolean);
const DESK_MODELS = PHONE_MODELS.filter((m) => m.lineKeys > 0);

/**
 * Setting up someone new, step by step: who they are, their own number and caller ID, the ring
 * groups and queues they answer, a desk phone, and how they sign in. One review at the end: the
 * whole setup is checked, applied and can be undone together.
 */
export function NewPerson() {
  const { tenant } = useTenant();
  const d = tenant.domain;
  const base = `/t/${encodeURIComponent(d)}`;
  const navigate = useNavigate();
  const people = useData(() => api.people(d), [d]);
  const directory = useData(() => api.directory(d).catch(() => null), [d]);
  const numbers = useData(() => api.accounts(d, 'number').catch(() => null), [d]);
  const groups = useData(() => api.accounts(d, 'ringGroup').catch(() => null), [d]);
  const queues = useData(() => api.accounts(d, 'callQueue').catch(() => null), [d]);
  const phones = useData(() => api.accounts(d, 'phone').catch(() => null), [d]);
  const zones = useData(() => api.options(d, 'timezone').catch(() => ({ options: [] })), [d]);
  const locations = useData(() => api.emergencyLocations(d).catch(() => null), [d]);

  const [params] = useSearchParams();
  const [step, setStep] = useState<StepId>('who');
  const [draft, setDraft] = useState<Draft | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [reviewing, setReviewing] = useState(false);
  const [reviewKey, setReviewKey] = useState(0);
  const [done, setDone] = useState<{ cs: ChangeSetView; invite: InviteResult | null; inviteError: string } | null>(null);

  const writableFields = people.data?.writableFields ?? [];
  const canSet = (f: string) => writableFields.includes(f);
  const writable = (people.data?.writable ?? false) && tenant.writable;
  const list = people.data?.people ?? [];
  // New extensions: 3 to 6 digits, as many as the company's others (2986).
  const { suggested, company } = useMemo(() => {
    const entries = [
      ...(directory.data?.entries ?? []).map((e) => (e.kind === 'person' && e.featureSet === MAILBOX_FEATURE_SET ? { ...e, kind: 'other' as const } : e)),
      ...list.filter((p) => p.featureSet !== MAILBOX_FEATURE_SET).map((p) => ({ extension: p.extension, kind: 'person' as const }))
    ];
    const company = companyLengthFrom(entries);
    return { company, suggested: suggestNumber('person', entries, { ...(directory.data?.mailboxPrefix ? { mailboxPrefix: directory.data.mailboxPrefix } : {}), ...(company ? { length: company.length } : {}) }) };
  }, [directory.data, list]);

  const loading = !people.data || directory.data === undefined || numbers.data === undefined || groups.data === undefined || queues.data === undefined || phones.data === undefined;
  if (people.error) {
    return (
      <>
        <PageHeader eyebrow="People" title="Add a person" />
        <div className="content">
          <Card>
            <LoadError error={people.error} retry={people.reload} />
          </Card>
        </div>
      </>
    );
  }
  if (loading) {
    return (
      <>
        <PageHeader eyebrow="People" title="Add a person" />
        <div className="content">
          <Card>
            <Loading label="Getting things ready" />
          </Card>
        </div>
      </>
    );
  }
  const addresses = (locations.data?.locations ?? []).filter((l) => l.active);
  const canLocate = canSet('emergencyLocation') && addresses.length > 0;
  const f: Draft = draft ?? {
    firstName: '',
    lastName: '',
    email: '',
    mobile: '',
    extension: suggested,
    featureSet: '',
    timeZone: '',
    number: '',
    callerId: '',
    // A company with one saved address: that's where they are.
    location: addresses.length === 1 ? (addresses[0]?.id ?? '') : '',
    groups: [],
    queues: [],
    noAnswerSeconds: '',
    forwardNoAnswer: '',
    phone: null,
    welcome: false,
    portal: false,
    place: params.get('place') === '1',
    placeKind: 'full'
  };
  const set = (patch: Partial<Draft>) => setDraft({ ...f, ...patch });
  for (const o of zones.data?.options ?? []) if (o.value) pbxZoneNames.set(o.value, o.label);

  // Platform trigger numbers (+1 899) are never anyone's own number.
  const numberList = (numbers.data?.accounts ?? []).filter((n) => !isInternalRoute(n.extension));
  const freeNumbers = numberList.filter((n) => !n.values.destination);
  const canNumber = Boolean(numbers.data?.writable) && tenant.writable;
  const groupList = groups.data?.accounts ?? [];
  const queueList = queues.data?.accounts ?? [];
  const canGroups = Boolean(groups.data?.writable) && tenant.writable;
  const canQueues = Boolean(queues.data?.writable) && tenant.writable;
  const canPhone = Boolean(phones.data?.writable) && tenant.writable;
  // A shared phone shows on phones as "Lobby Phone" (the phone system wants two names).
  const PLACE_LAST = 'Phone';
  const name = f.place ? f.firstName.trim() || 'the shared phone' : `${f.firstName.trim()} ${f.lastName.trim()}`.trim() || 'them';
  const first = f.firstName.trim() || 'them';
  const nameOfNumber = (ext: string) => directory.data?.entries.find((e) => e.extension === ext)?.name || ext;

  // --- checking each step before moving on
  const check = (id: StepId): Record<string, string> => {
    const e: Record<string, string> = {};
    if (id === 'who') {
      if (!f.firstName.trim()) e.firstName = f.place ? 'Name the place (Lobby, Kitchen)' : 'Enter their first name';
      if (!f.place && !f.lastName.trim()) e.lastName = 'Enter their last name';
      const problem = extensionProblem(f.extension, company);
      if (problem) e.extension = problem;
      else if ((directory.data?.entries ?? []).some((x) => x.extension === f.extension.trim()) || list.some((p) => p.extension === f.extension.trim())) e.extension = 'This number is already in use';
      if (f.email.trim() && !EMAIL.test(f.email.trim())) e.email = 'Not an email address';
      if (f.mobile.trim() && !/^\+?[0-9 ().-]{7,24}$/.test(f.mobile.trim())) e.mobile = 'Not a phone number';
    }
    // A shared phone needs its own 911 address, down to the room or floor.
    if (id === 'number' && f.place && canLocate && !f.location) e.location = 'A shared phone needs its own 911 address';
    if (id === 'calls' && f.noAnswerSeconds && (!/^\d{1,3}$/.test(f.noAnswerSeconds) || Number(f.noAnswerSeconds) < 5 || Number(f.noAnswerSeconds) > 120)) e.noAnswerSeconds = 'Between 5 and 120 seconds, or empty';
    if (id === 'phone' && f.phone) {
      const mac = normalizeMac(f.phone.mac);
      if (!mac) e.mac = 'A MAC address is 12 letters and digits, printed on a label under the phone';
      else if ((phones.data?.accounts ?? []).some((p) => p.extension === mac)) e.mac = 'That phone is already on your phone system';
    }
    return e;
  };
  const index = STEPS.findIndex((s) => s.id === step);
  const goTo = (id: StepId) => {
    // Forward only once every step before is complete.
    const target = STEPS.findIndex((s) => s.id === id);
    for (let i = 0; i < target; i++) {
      const e = check(STEPS[i]!.id);
      if (Object.keys(e).length) {
        setErrors(e);
        setStep(STEPS[i]!.id);
        return;
      }
    }
    setErrors({});
    setStep(id);
    window.scrollTo({ top: 0 });
  };

  // --- what will happen, as changes
  const items: ReviewItem[] = [];
  const ext = f.extension.trim();
  const ownNumber = f.number || '';
  const callerId = f.callerId === 'own' && ownNumber && canSet('callerId') ? ownNumber : '';
  const extras = [
    f.place ? `${sharedPhoneKindLabel(f.placeKind)} shared phone` : f.featureSet ? ALL_FEATURE_SETS.find((o) => o.value === f.featureSet)?.label : '',
    f.timeZone && canSet('timeZone') ? `time zone ${pbxZoneNames.get(f.timeZone) ?? f.timeZone}` : '',
    callerId ? `caller ID ${formatPhoneNumber(callerId)}` : '',
    f.noAnswerSeconds && canSet('noAnswerSeconds') ? `rings for ${f.noAnswerSeconds} seconds` : '',
    f.forwardNoAnswer && canSet('forwardNoAnswer') ? `unanswered calls go to ${nameOfNumber(f.forwardNoAnswer)}` : '',
    f.location && canLocate ? `911 address ${addresses.find((l) => l.id === f.location)?.name || 'chosen'}` : ''
  ].filter(Boolean);
  items.push({
    key: 'person',
    description: `Add ${name} at extension ${ext}${extras.length ? `: ${extras.join(', ')}` : ''}`,
    intent: {
      op: 'person.create',
      extension: ext,
      firstName: f.firstName.trim(),
      lastName: f.place ? PLACE_LAST : f.lastName.trim(),
      ...(f.email.trim() && !f.place ? { email: f.email.trim() } : {}),
      ...(f.mobile.trim() && !f.place ? { mobile: f.mobile.trim() } : {}),
      ...(f.featureSet ? { featureSet: f.featureSet } : {}),
      ...(f.timeZone && canSet('timeZone') ? { timeZone: f.timeZone } : {}),
      ...(callerId ? { callerId } : {}),
      ...(f.noAnswerSeconds && canSet('noAnswerSeconds') ? { noAnswerSeconds: f.noAnswerSeconds } : {}),
      ...(f.forwardNoAnswer && canSet('forwardNoAnswer') ? { forwardNoAnswer: f.forwardNoAnswer } : {}),
      ...(f.location && canLocate ? { emergencyLocation: f.location } : {})
    } as Intent
  });
  if (ownNumber && canNumber) {
    const n = numberList.find((x) => x.extension === ownNumber);
    items.push({
      key: 'number',
      description: `Send calls to ${formatPhoneNumber(ownNumber)} to ${name}${n?.values.destination ? ` (now ${nameOfNumber(n.values.destination)})` : ''}`,
      intent: { op: 'account.update', kind: 'number', extension: ownNumber, set: { destination: ext } } as Intent
    });
  }
  for (const g of f.groups) {
    const acc = groupList.find((x) => x.extension === g);
    if (!acc) continue;
    items.push({
      key: `group-${g}`,
      description: `Ring ${first} in ${displayOf('ringGroup', g, acc.values)}`,
      intent: { op: 'account.update', kind: 'ringGroup', extension: g, set: { stage1: [...words(acc.values.stage1), ext].join(' ') } } as Intent
    });
  }
  for (const q of f.queues) {
    const acc = queueList.find((x) => x.extension === q);
    if (!acc) continue;
    items.push({
      key: `queue-${q}`,
      description: `Make ${first} an agent of ${displayOf('callQueue', q, acc.values)}`,
      intent: { op: 'account.update', kind: 'callQueue', extension: q, set: { agents: [...words(acc.values.agents), ext].join(' ') } } as Intent
    });
  }
  const mac = f.phone ? normalizeMac(f.phone.mac) : null;
  if (f.phone && mac && canPhone) {
    const m = phoneModel(f.phone.model);
    items.push({
      key: 'phone',
      description: `Add a ${m ? `${m.vendor} ${m.model}` : 'desk phone'} for ${first}`,
      intent: { op: 'account.create', kind: 'phone', extension: mac, values: { model: f.phone.model, user: ext } } as Intent
    });
  }
  if (f.welcome && f.email.trim() && !f.place) items.push({ key: 'welcome', description: `Email ${first} their login details for the apps`, intent: { op: 'notify.welcome', extension: ext } as Intent });

  async function applied(cs: ChangeSetView) {
    let invite: InviteResult | null = null;
    let inviteError = '';
    // A shared phone: Stitch keeps it as a place (listed apart, not a person, no sign-in).
    if (f.place) {
      try {
        await placesApi.set(d, ext, { name: f.firstName.trim(), kind: f.placeKind });
      } catch (err) {
        inviteError = `The phone was added, but Stitch couldn’t mark it as a shared phone: ${err instanceof Error ? err.message : 'try again from its page'}.`;
      }
    }
    if (f.portal && f.email.trim() && !f.place) {
      try {
        invite = await api.invite(d, { email: f.email.trim(), name, role: 'user', extension: ext });
      } catch (err) {
        inviteError = err instanceof ApiError || err instanceof Error ? err.message : 'The invitation didn’t send.';
      }
    }
    setDone({ cs, invite, inviteError });
  }

  if (done) {
    return (
      <>
        <PageHeader eyebrow="People" title={`${name} is set up`} />
        <div className="content">
          <Card>
            <div className="card-body section-stack wizard-done">
              <span className="done-mark" aria-hidden="true">
                <Icon name="check" size={22} strokeWidth={2.6} />
              </span>
              <p>
                Every change was checked on the phone system{done.cs.undoUntil ? ', and can be undone from Change history for 30 days' : ''}.
              </p>
              {done.invite && <ShareLink result={done.invite} />}
              {done.inviteError && <Notice tone="crit">{done.inviteError}</Notice>}
              <div className="wizard-actions">
                <Link className="btn primary" to={`${base}/people/${encodeURIComponent(ext)}`}>
                  Open {first}’s page
                </Link>
                <Button
                  variant="secondary"
                  onClick={() => {
                    setDone(null);
                    setDraft(null);
                    setStep('who');
                    people.reload();
                    directory.reload();
                    numbers.reload();
                  }}
                >
                  Add another person
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        eyebrow={
          <>
            <Link to={`${base}/people`}>People</Link> · New person
          </>
        }
        title={f.place ? 'Add a shared phone' : 'Add a person'}
      />
      <div className="content wizard">
        {!writable && (
          <Notice tone="warn" title="View-only" icon="lock">
            {OFF.readOnly}
          </Notice>
        )}
        <div className="wizard-layout">
          <nav className="wizard-steps" aria-label="Steps">
            <ol>
              {STEPS.map((s, i) => (
                <li key={s.id} className={s.id === step ? 'current' : i < index ? 'past' : undefined}>
                  <button type="button" onClick={() => goTo(s.id)} aria-current={s.id === step ? 'step' : undefined}>
                    <span className="wizard-num" aria-hidden="true">
                      {i < index ? <Icon name="check" size={13} strokeWidth={2.6} /> : i + 1}
                    </span>
                    {s.title}
                  </button>
                </li>
              ))}
            </ol>
          </nav>
          <Card title={STEPS[index]!.title}>
            <form
              className="card-body wizard-body"
              noValidate
              onSubmit={(e) => {
                e.preventDefault();
                const next = STEPS[index + 1];
                if (next) goTo(next.id);
              }}
            >
              {step === 'who' && (
                <>
                  <div className="radio-cards compact" role="radiogroup" aria-label="What it is">
                    <label className="radio-card">
                      <input type="radio" name="what" checked={!f.place} onChange={() => set({ place: false, featureSet: '' })} />
                      <span>
                        <strong>A person</strong>
                        <span className="muted"> Someone with their own extension, apps and sign-in.</span>
                      </span>
                    </label>
                    <label className="radio-card">
                      <input type="radio" name="what" checked={f.place} onChange={() => set({ place: true, welcome: false, portal: false, featureSet: canSet('featureSet') ? sharedPhoneFeatureSet(f.placeKind) : '' })} />
                      <span>
                        <strong>A shared phone</strong>
                        <span className="muted"> A lobby, kitchen, conference room or classroom phone, or a hot desk: no person, no sign-in, its own 911 address.</span>
                      </span>
                    </label>
                  </div>
                  {f.place ? (
                    <>
                      <Field
                        label="Place name"
                        value={f.firstName}
                        onChange={(e) => set({ firstName: e.target.value })}
                        error={errors.firstName}
                        autoComplete="off"
                        autoFocus
                        placeholder="Lobby, Kitchen, Conference room B"
                        hint={f.firstName.trim() ? `Shows on phones as “${f.firstName.trim()} ${PLACE_LAST}”.` : 'Shown on phones and in the directory.'}
                      />
                      <div className="field">
                        <span className="field-label">Kind</span>
                        <div className="radio-cards compact" role="radiogroup" aria-label="Kind">
                          {SHARED_PHONE_KINDS.map((k) => {
                            // Every kind but Full is its own extension type, set on the phone system with the rest.
                            const open = k.kind === 'full' || canSet('featureSet');
                            return (
                              <label className={`radio-card${open ? '' : ' disabled'}`} key={k.kind}>
                                <input
                                  type="radio"
                                  name="placeKind"
                                  checked={f.placeKind === k.kind}
                                  disabled={!open}
                                  onChange={() => set({ placeKind: k.kind, featureSet: canSet('featureSet') ? sharedPhoneFeatureSet(k.kind) : '' })}
                                />
                                <span>
                                  <strong>{k.label}</strong>
                                  <span className="muted"> {k.description}</span>
                                </span>
                              </label>
                            );
                          })}
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                  <div className="form-grid">
                    <Field label="First name" value={f.firstName} onChange={(e) => set({ firstName: e.target.value })} error={errors.firstName} autoComplete="off" autoFocus />
                    <Field label="Last name" value={f.lastName} onChange={(e) => set({ lastName: e.target.value })} error={errors.lastName} autoComplete="off" />
                  </div>
                  <Field label="Email" type="email" value={f.email} onChange={(e) => set({ email: e.target.value })} error={errors.email} hint="For voicemail, their app login and signing in to Stitch. Optional." autoComplete="off" />
                    </>
                  )}
                  {!f.place && people.data!.fields.includes('mobile') && (
                    <Field label="Mobile" type="tel" value={f.mobile} onChange={(e) => set({ mobile: e.target.value })} error={errors.mobile} hint="Optional." autoComplete="off" />
                  )}
                  <Field
                    label="Extension"
                    inputMode="numeric"
                    value={f.extension}
                    onChange={(e) => set({ extension: e.target.value })}
                    error={errors.extension}
                    hint={f.extension === suggested ? 'The next free number. Change it if you like.' : extensionHint(company)}
                  />
                  {canSet('featureSet') && !f.place && (
                    <div className="field">
                      <span className="field-label">What they’ll use</span>
                      <div className="radio-cards compact" role="radiogroup" aria-label="Feature set">
                        {FEATURE_SETS.filter((o) => o.value !== MAILBOX_FEATURE_SET).map((o) => (
                          <label className="radio-card" key={o.value || 'standard'}>
                            <input type="radio" name="featureSet" checked={f.featureSet === o.value} onChange={() => set({ featureSet: o.value })} />
                            <span>
                              <strong>{o.label}</strong>
                              <span className="muted"> {o.description}</span>
                            </span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                  {canSet('timeZone') && (
                    <div className="field">
                      <label htmlFor="np-tz">Time zone</label>
                      <select id="np-tz" className="input" value={f.timeZone} onChange={(e) => set({ timeZone: e.target.value })}>
                        {(zones.data?.options.length ? zones.data.options : [{ value: '', label: 'Default' }]).map((o) => (
                          <option key={o.value || 'default'} value={o.value}>
                            {o.value ? o.label : 'The company’s (default)'}
                          </option>
                        ))}
                      </select>
                      <span className="hint">Only if they work somewhere else.</span>
                    </div>
                  )}
                </>
              )}

              {step === 'number' && (
                <>
                  <p className="wizard-lede">A number of their own rings them directly. Without one, callers reach them by extension, or through your menu, ring groups and queues.</p>
                  {!numbers.data ? (
                    <Notice>Phone numbers can’t be read from this phone system yet. Stitch support can give them one later.</Notice>
                  ) : !canNumber ? (
                    <Notice icon="lock">Numbers can’t be changed here yet. Stitch support can give them one.</Notice>
                  ) : (
                    <fieldset className="plain-fieldset">
                      <legend className="sr-only">Their number</legend>
                      <label className="radio-card">
                        <input type="radio" name="np-number" checked={!f.number} onChange={() => set({ number: '', callerId: '' })} />
                        <span className="cell-stack">
                          <strong>No number of their own</strong>
                          <small>Callers reach them by extension {f.extension.trim()}.</small>
                        </span>
                      </label>
                      {freeNumbers.map((n) => (
                        <NumberOption key={n.extension} n={n} checked={f.number === n.extension} onPick={() => set({ number: n.extension, callerId: canSet('callerId') ? 'own' : '' })} sub={n.live?.number?.label ?? 'Not going anywhere yet'} />
                      ))}
                      {freeNumbers.length === 0 && <span className="hint">Every number goes somewhere already. Get a new one on Numbers, or ask Stitch support.</span>}
                      {numberList.filter((n) => n.values.destination).length > 0 && (
                        <details className="wizard-more">
                          <summary>Use a number that goes somewhere else now</summary>
                          {numberList
                            .filter((n) => n.values.destination)
                            .map((n) => (
                              <NumberOption key={n.extension} n={n} checked={f.number === n.extension} onPick={() => set({ number: n.extension, callerId: canSet('callerId') ? 'own' : '' })} sub={`Now: ${nameOfNumber(n.values.destination ?? '')}. It moves to ${first}.`} />
                            ))}
                        </details>
                      )}
                    </fieldset>
                  )}
                  {canSet('callerId') && (
                    <fieldset className="plain-fieldset">
                      <legend className="field-label">When they call out, people see</legend>
                      <label className="radio-card">
                        <input type="radio" name="np-cid" checked={f.callerId === ''} onChange={() => set({ callerId: '' })} />
                        <span className="cell-stack">
                          <strong>The company’s usual number</strong>
                          <small>Your main number, as set on the phone system.</small>
                        </span>
                      </label>
                      <label className={`radio-card${f.number ? '' : ' disabled'}`}>
                        <input type="radio" name="np-cid" checked={f.callerId === 'own'} disabled={!f.number} onChange={() => set({ callerId: 'own' })} />
                        <span className="cell-stack">
                          <strong>{f.number ? `Their own number, ${formatPhoneNumber(f.number)}` : 'Their own number'}</strong>
                          <small>{f.number ? 'People can call them straight back.' : 'Give them a number above first.'}</small>
                        </span>
                      </label>
                    </fieldset>
                  )}
                  {canLocate && (
                    <div className="field">
                      <label className="field-label" htmlFor="np-e911">
                        {f.place ? 'Emergency address (911) for this phone, down to the room or floor' : 'Emergency address (911) for their phone'}
                      </label>
                      <select id="np-e911" className="input" value={f.location} aria-invalid={errors.location ? true : undefined} onChange={(e) => set({ location: e.target.value })}>
                        <option value="">{f.place ? 'Choose one' : 'Choose later'}</option>
                        {addresses.map((l) => (
                          <option key={l.id} value={l.id}>
                            {[l.name, l.address].filter(Boolean).join(' · ')}
                          </option>
                        ))}
                      </select>
                      <span className="hint">
                        {f.place
                          ? 'Anyone may dial 911 from it, so it needs a dispatchable address. Add one for the room on Emergency addresses if it isn’t listed.'
                          : 'Where emergency services are sent when they call 911. Choose where they actually work.'}
                      </span>
                      {errors.location && <span className="error">{errors.location}</span>}
                    </div>
                  )}
                  <div className="setting-row e911">
                    <span className="cell-stack">
                      <strong>Their number’s 911 address</strong>
                      <span className="muted">
                        {f.number ? `Once they’re added, check where 911 sends help for ${formatPhoneNumber(f.number)} on ` : 'Each number’s 911 address is on '}
                        <Link to={`${base}/emergency`}>Emergency addresses</Link>.
                      </span>
                    </span>
                  </div>
                </>
              )}

              {step === 'calls' && (
                <>
                  {groupList.length > 0 && (
                    <fieldset className="plain-fieldset">
                      <legend className="field-label">Ring them in these ring groups</legend>
                      {!canGroups && <span className="hint">Ring groups can’t be changed here yet: Stitch support can add them.</span>}
                      {groupList.map((g) => (
                        <label key={g.extension} className="check">
                          <input
                            type="checkbox"
                            disabled={!canGroups}
                            checked={f.groups.includes(g.extension)}
                            onChange={(e) => set({ groups: e.target.checked ? [...f.groups, g.extension] : f.groups.filter((x) => x !== g.extension) })}
                          />
                          {displayOf('ringGroup', g.extension, g.values)} <span className="muted">({g.extension})</span>
                        </label>
                      ))}
                    </fieldset>
                  )}
                  {queueList.length > 0 && (
                    <fieldset className="plain-fieldset">
                      <legend className="field-label">Answer these call queues</legend>
                      {!canQueues && <span className="hint">Call queues can’t be changed here yet: Stitch support can add them.</span>}
                      {queueList.map((q) => (
                        <label key={q.extension} className="check">
                          <input
                            type="checkbox"
                            disabled={!canQueues}
                            checked={f.queues.includes(q.extension)}
                            onChange={(e) => set({ queues: e.target.checked ? [...f.queues, q.extension] : f.queues.filter((x) => x !== q.extension) })}
                          />
                          {displayOf('callQueue', q.extension, q.values)} <span className="muted">({q.extension})</span>
                        </label>
                      ))}
                      {f.queues.length > 0 && f.featureSet !== AGENT_FEATURE_SET && canSet('featureSet') && !(f.place && f.placeKind !== 'full') && (
                        <span className="hint">They’ll be switched to the Call queue agent feature set, so they can take queue calls.</span>
                      )}
                    </fieldset>
                  )}
                  {canSet('noAnswerSeconds') && (
                    <Field label="Ring them for (seconds)" inputMode="numeric" value={f.noAnswerSeconds} onChange={(e) => set({ noAnswerSeconds: e.target.value })} error={errors.noAnswerSeconds} hint="Before calls go to voicemail. Empty: the usual time." />
                  )}
                  {canSet('forwardNoAnswer') && (
                    <DestinationPicker
                      label="When they don’t answer, send calls to"
                      value={f.forwardNoAnswer}
                      onChange={(v) => set({ forwardNoAnswer: v })}
                      dir={directory.data ?? undefined}
                      help="Empty: their voicemail. Their mobile, a colleague or any number."
                    />
                  )}
                  {groupList.length === 0 && queueList.length === 0 && !canSet('noAnswerSeconds') && !canSet('forwardNoAnswer') && (
                    <p className="wizard-lede">Calls to their extension (and their number) ring them, then go to their voicemail. You can add them to ring groups and queues later.</p>
                  )}
                </>
              )}

              {step === 'phone' && (
                <>
                  {!canPhone ? (
                    <Notice icon="lock">Desk phones can’t be added here yet. Stitch support can set one up, or {first} can use the apps.</Notice>
                  ) : (
                    <fieldset className="plain-fieldset">
                      <legend className="sr-only">Desk phone</legend>
                      <label className="radio-card">
                        <input type="radio" name="np-phone" checked={!f.phone} onChange={() => set({ phone: null })} />
                        <span className="cell-stack">
                          <strong>No desk phone</strong>
                          <small>They’ll use the apps, or you can add a phone later.</small>
                        </span>
                      </label>
                      <label className="radio-card">
                        <input type="radio" name="np-phone" checked={Boolean(f.phone)} onChange={() => set({ phone: { mac: '', model: DESK_MODELS[0]?.id ?? '' } })} />
                        <span className="cell-stack">
                          <strong>Add a desk phone for them</strong>
                          <small>It picks up their line the next time it restarts.</small>
                        </span>
                      </label>
                      {f.phone && (
                        <div className="form-grid">
                          <Field label="MAC address" value={f.phone.mac} onChange={(e) => set({ phone: { ...f.phone!, mac: e.target.value } })} error={errors.mac} hint="On a label under the phone." autoComplete="off" />
                          <div className="field">
                            <label htmlFor="np-model">Model</label>
                            <select id="np-model" className="input" value={f.phone.model} onChange={(e) => set({ phone: { ...f.phone!, model: e.target.value } })}>
                              {DESK_MODELS.map((m) => (
                                <option key={m.id} value={m.id}>
                                  {m.vendor} {m.model}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      )}
                    </fieldset>
                  )}
                </>
              )}

              {step === 'access' && f.place && <p>A shared phone has no sign-in and gets no welcome email. Anyone can use it; it shows as a place in the directory and the receptionist console.</p>}
              {step === 'access' && !f.place && (
                <>
                  <label className="check">
                    <input type="checkbox" checked={f.welcome} disabled={!f.email.trim()} onChange={(e) => set({ welcome: e.target.checked })} />
                    <span>
                      Email {first} their login details for the apps
                      <span className="hint"> From the phone system, to {f.email.trim() || 'their email (add one on the first step)'}.</span>
                    </span>
                  </label>
                  <label className="check">
                    <input type="checkbox" checked={f.portal} disabled={!f.email.trim()} onChange={(e) => set({ portal: e.target.checked })} />
                    <span>
                      Invite {first} to Stitch to see their own phone
                      <span className="hint"> Their desk phone keys, their queues and their calls. They choose a password from the invitation.</span>
                    </span>
                  </label>
                </>
              )}

              {step === 'review' && (
                <>
                  <p className="wizard-lede">Here’s everything that will happen. It’s checked against your phone system before anything changes, and can be undone together for 30 days.</p>
                  <ol className="wizard-review">
                    {items.map((i) => (
                      <li key={i.key}>
                        <Icon name="check" size={15} strokeWidth={2.4} /> {i.description}
                      </li>
                    ))}
                    {f.portal && f.email.trim() && (
                      <li>
                        <Icon name="mail" size={15} /> Invite {first} to Stitch to see their own phone (sent once the rest is done)
                      </li>
                    )}
                  </ol>
                </>
              )}

              <div className="wizard-actions">
                {index > 0 && (
                  <Button variant="ghost" onClick={() => goTo(STEPS[index - 1]!.id)}>
                    Back
                  </Button>
                )}
                {step !== 'review' ? (
                  <Button variant="primary" type="submit">
                    Next: {STEPS[index + 1]!.title}
                  </Button>
                ) : (
                  <Button
                    variant="primary"
                    disabled={!writable}
                    onClick={() => {
                      setReviewKey((k) => k + 1);
                      setReviewing(true);
                    }}
                  >
                    Check and add {first}
                  </Button>
                )}
                <Button variant="ghost" onClick={() => navigate(`${base}/people`)}>
                  Cancel
                </Button>
              </div>
            </form>
          </Card>
        </div>
      </div>
      <ReviewDrawer
        key={reviewKey}
        open={reviewing}
        tenant={d}
        items={items}
        onUnstage={() => undefined}
        onApplied={(cs) => void applied(cs)}
        onClose={() => setReviewing(false)}
      />
    </>
  );
}

function NumberOption({ n, checked, onPick, sub }: { n: Account; checked: boolean; onPick: () => void; sub: string }) {
  return (
    <label className="radio-card">
      <input type="radio" name="np-number" checked={checked} onChange={onPick} />
      <span className="cell-stack">
        <strong>{formatPhoneNumber(n.extension)}</strong>
        <small>{sub}</small>
      </span>
    </label>
  );
}
